/**
 * PairsEditor: a name-to-value map as rows of two inputs, for the harness
 * maps (options, routes, aliases). Rows are appended and removed, never
 * reordered, and every input carries a label that names the map and the
 * row, since the map has no column headers.
 */

import { Plus, X } from 'lucide-react'

import { RowAction } from '@/components/settings/RowAction'
import { Button } from '@/components/ui/button'
import { EYEBROW } from '@/components/ui/eyebrow'
import { Input } from '@/components/ui/input'

import type { PairRow } from './benchmarkSources'

interface PairsEditorProps {
  /** Prefix for the inputs' ids, unique on the page. */
  id: string
  title: string
  hint: string
  keyLabel: string
  valueLabel: string
  keyPlaceholder: string
  valuePlaceholder: string
  addLabel: string
  rows: PairRow[]
  onChange: (next: (previous: PairRow[]) => PairRow[]) => void
}

export function PairsEditor({
  id,
  title,
  hint,
  keyLabel,
  valueLabel,
  keyPlaceholder,
  valuePlaceholder,
  addLabel,
  rows,
  onChange,
}: PairsEditorProps): React.JSX.Element {
  const hintId = `${id}-hint`
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className={EYEBROW}>{title}</p>
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      </div>
      {rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((row, index) => {
            const n = String(index + 1)
            return (
              // Rows are appended and removed, never reordered.
              <li key={index} className="flex items-center gap-2">
                <Input
                  id={`${id}-${n}-key`}
                  className="font-mono"
                  value={row.key}
                  autoComplete="off"
                  aria-label={`${title} ${n} ${keyLabel}`}
                  aria-describedby={hintId}
                  placeholder={keyPlaceholder}
                  onChange={(e) => {
                    const key = e.target.value
                    onChange((previous) =>
                      previous.map((pair, i) => (i === index ? { ...pair, key } : pair)),
                    )
                  }}
                />
                <Input
                  id={`${id}-${n}-value`}
                  className="font-mono"
                  value={row.value}
                  autoComplete="off"
                  aria-label={`${title} ${n} ${valueLabel}`}
                  placeholder={valuePlaceholder}
                  onChange={(e) => {
                    const value = e.target.value
                    onChange((previous) =>
                      previous.map((pair, i) => (i === index ? { ...pair, value } : pair)),
                    )
                  }}
                />
                <RowAction
                  label={`Remove ${title.toLowerCase()} ${n}`}
                  icon={X}
                  destructive
                  onClick={() => {
                    onChange((previous) => previous.filter((_pair, i) => i !== index))
                  }}
                />
              </li>
            )
          })}
        </ul>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          onChange((previous) => [...previous, { key: '', value: '' }])
        }}
      >
        <Plus aria-hidden="true" />
        {addLabel}
      </Button>
    </div>
  )
}
